import React, { useEffect, useMemo, useRef, useState } from "react";

import {
  Check,
  ChevronsDownUp,
  ChevronsUpDown,
  Eye,
  MoreVertical,
  Pencil,
  Upload,
} from "lucide-react";
import { useDispatch } from "react-redux";

import { ReactComponent as DragHandleIcon } from "@/assets/svg/dots-grid.svg";
import { FileIcon } from "@/components/ui/file-icon";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { ScrollArea, ScrollBar } from "@/components/ui/scroll-area";
import { DOCUMENT_CLASSIFICATIONS, DocumentCategory } from "@/constants/documents";
import { useExportCase } from "@/hooks/mutations/use-export-case";
import { useUpdateFileClassification } from "@/hooks/mutations/use-files";
import { setMessage } from "@/redux/notification";
import { cn } from "@/utils/ui";

import { DocSorterFileSchema } from "@api/generated/types.gen";

import { useSorting } from "@components/DataTables/hooks/use-sorting";
import {
  DeleteFileConfirmation,
  EditDocumentDateModal,
  EditDocumentDescriptionModal,
  RegenerateFileConfirmation,
} from "@components/FilesListDataTable";
import { Button } from "@components/ui/button";
import { Checkbox } from "@components/ui/checkbox";
import { Tooltip, TooltipContent, TooltipTrigger } from "@components/ui/tooltip";

import { stripBundlePrefix } from "@utils/bundle-name";
import { formatNullableDateToLocal } from "@utils/dateUtils";

import { useDragAndDrop } from "../hooks/useDragAndDrop";
import { DocumentTypeHeader } from "./DocumentTypeHeader";
import { ManualClassificationModal } from "./ManualClassificationModal";
import { SortableColumnHeader } from "./SortableColumnHeader";
import { SummaryStatusCell } from "./SummaryStatusCell";

type DocumentTypeViewProps = {
  files: DocSorterFileSchema[];
  onViewFile: (file: DocSorterFileSchema | null) => void;
  onViewSummary: (file: DocSorterFileSchema) => void;
  isLoading: boolean;
  selectedDocumentId?: string;
  showSummaryPanel?: boolean;
  caseId?: string; // Case ID for export functionality
  isMergeMode?: boolean;
  mergeSelectedFiles?: string[];
  onSelectFileForMerge?: (fileId: string, selected: boolean) => void;
  onSelectAllForMerge?: (selected: boolean) => void;
  onUnmergeFile?: (fileId: string, fileName: string) => void;
};

export const DocumentTypeView = ({
  files,
  onViewFile,
  onViewSummary,
  isLoading,
  selectedDocumentId,
  showSummaryPanel = false,
  caseId,
  isMergeMode = false,
  mergeSelectedFiles = [],
  onSelectFileForMerge,
  onSelectAllForMerge,
  onUnmergeFile,
}: DocumentTypeViewProps) => {
  const sorting = useSorting();

  const sortColumns = useMemo(
    () => [
      { id: "short_summary", accessor: (f: DocSorterFileSchema) => f.short_summary },
      { id: "date", accessor: (f: DocSorterFileSchema) => f.date },
      { id: "created_at", accessor: (f: DocSorterFileSchema) => f.created_at },
      { id: "name", accessor: (f: DocSorterFileSchema) => f.name },
    ],
    []
  );

  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(new Set());
  const [editingFile, setEditingFile] = useState<DocSorterFileSchema | null>(null);
  const [isEditDateModalOpen, setIsEditDateModalOpen] = useState(false);
  const [editingDescriptionFile, setEditingDescriptionFile] =
    useState<DocSorterFileSchema | null>(null);
  const [isEditDescriptionModalOpen, setIsEditDescriptionModalOpen] =
    useState(false);
  const [manualClassificationFile, setManualClassificationFile] =
    useState<DocSorterFileSchema | null>(null);
  const [isManualClassificationModalOpen, setIsManualClassificationModalOpen] =
    useState(false);
  const [openPopoverId, setOpenPopoverId] = useState<string | null>(null);
  const tableContainerRef = useRef<HTMLDivElement>(null);

  const {
    dragState,
    handleDragStart,
    handleDragOver,
    handleDragEnter,
    handleDragLeave,
    handleDrop,
    handleDragEnd,
    canDropInZone,
    setScrollContainer,
  } = useDragAndDrop({
    scrollSpeed: 15,
    scrollThreshold: 80,
  });

  const dispatch = useDispatch();

  const exportCase = useExportCase();

  const updateClassificationMutation = useUpdateFileClassification({
    onSuccess: (_data, _fileId, classification) => {
      dispatch(
        setMessage({
          content: `File classification updated to "${classification}" successfully`,
          type: "success",
        })
      );
    },
    onError: (error) => {
      dispatch(
        setMessage({
          content: error.message || "Failed to update file classification",
          type: "error",
        })
      );
    },
  });

  // Update the scroll container reference whenever it changes
  useEffect(() => {
    if (tableContainerRef.current) {
      setScrollContainer(tableContainerRef.current);
    }
  }, [setScrollContainer]);

  const groupedFiles = useMemo(() => {
    const groups: Record<string, DocSorterFileSchema[]> = {};

    DOCUMENT_CLASSIFICATIONS.forEach((docType) => {
      groups[docType.category] = [];
    });

    files.forEach((file) => {
      const category = file.classification || "Uncategorized";

      if (!groups[category]) {
        groups[category] = [];
      }
      groups[category].push(file);
    });

    return groups;
  }, [files]);

  const allCategories = useMemo(() => {
    return DOCUMENT_CLASSIFICATIONS.map((docType) => docType.category);
  }, []);

  const toggleGroupCollapse = (category: string) => {
    setCollapsedGroups((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(category)) {
        newSet.delete(category);
      } else {
        newSet.add(category);
      }
      return newSet;
    });
  };

  const handleFileDragStart = (
    file: DocSorterFileSchema,
    sourceCategory: string
  ) => {
    handleDragStart(file, sourceCategory);
  };

  const handleCategoryDrop = async (
    event: React.DragEvent,
    targetCategory: string
  ) => {
    const dropResult = handleDrop(event, targetCategory);

    if (dropResult && dropResult.sourceCategory !== dropResult.targetCategory) {
      // Prevent moving another file into LOI if an LOI already exists
      const targetIsLOI = targetCategory === DocumentCategory.LOI;
      if (targetIsLOI) {
        const existingInTarget = groupedFiles[targetCategory] || [];
        // If there is any file in the LOI category that's not the file being moved, block it
        const hasOtherLOI = existingInTarget.some(
          (f) => f.id !== dropResult.file.id
        );
        if (hasOtherLOI) {
          dispatch(
            setMessage({
              content:
                "Only one LOI is allowed. Please remove the existing LOI before moving another file into this category.",
              type: "warning",
            })
          );
          return;
        }
      }

      try {
        // Update the file classification
        await updateClassificationMutation.mutateAsync({
          fileId: dropResult.file.id,
          classification: dropResult.targetCategory,
        });

        // Export the specific file after successful classification update
        if (caseId) {
          await exportCase.mutateAsync({
            caseId,
            fileIds: [dropResult.file.id],
          });
        }
      } catch (error) {
        console.error("Error updating classification or exporting file:", error);
      }
    }
  };

  const handleEditDate = (file: DocSorterFileSchema) => {
    setEditingFile(file);
    setIsEditDateModalOpen(true);
  };

  const handleCloseEditDateModal = () => {
    setEditingFile(null);
    setIsEditDateModalOpen(false);
  };

  const handleEditDescription = (file: DocSorterFileSchema) => {
    setEditingDescriptionFile(file);
    setIsEditDescriptionModalOpen(true);
  };

  const handleCloseEditDescriptionModal = () => {
    setEditingDescriptionFile(null);
    setIsEditDescriptionModalOpen(false);
  };

  const handleOpenManualClassification = (file: DocSorterFileSchema) => {
    setManualClassificationFile(file);
    setIsManualClassificationModalOpen(true);
    setOpenPopoverId(null);
  };

  const handleCloseManualClassificationModal = () => {
    setManualClassificationFile(null);
    setIsManualClassificationModalOpen(false);
  };

  const handleManualClassificationSave = async (
    fileId: string,
    classification: string
  ) => {
    if (!manualClassificationFile) return;

    // Check if the classification is the same as the current one
    const currentClassification = manualClassificationFile.classification || "";
    if (currentClassification === classification) {
      // No change, just close the modal
      handleCloseManualClassificationModal();
      return;
    }

    try {
      // Update the file classification
      await updateClassificationMutation.mutateAsync({
        fileId,
        classification,
      });

      // Export the specific file after successful classification update
      if (caseId) {
        await exportCase.mutateAsync({
          caseId,
          fileIds: [fileId],
        });
      }

      handleCloseManualClassificationModal();
    } catch (error) {
      console.error("Error updating classification or exporting file:", error);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="text-center">
          <div className="mx-auto mb-4 h-8 w-8 animate-spin rounded-full border-b-2 border-primary"></div>
          <p className="text-sm text-foreground-muted">Loading documents...</p>
        </div>
      </div>
    );
  }

  if (files.length === 0) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="text-center">
          <p className="text-sm text-foreground-muted">No documents found</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <ScrollArea ref={tableContainerRef} className="h-full w-full overflow-auto">
        <div className="w-full min-w-0 flex-1">
          <div className="sticky top-0 z-10 flex w-full min-w-[800px] items-center border-b bg-background p-5 pl-4">
            <div className="w-8 flex-shrink-0">
              {/* Expand / Collapse all button (icon with tooltip) */}
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="size-6 text-foreground-muted hover:bg-transparent hover:text-primary"
                    onClick={() => {
                      if (collapsedGroups.size === 0) {
                        // collapse all
                        setCollapsedGroups(new Set(allCategories));
                      } else {
                        // expand all
                        setCollapsedGroups(new Set());
                      }
                    }}
                    aria-label={
                      collapsedGroups.size === 0 ? "Collapse all" : "Expand all"
                    }
                    title={
                      collapsedGroups.size === 0 ? "Collapse all" : "Expand all"
                    }
                  >
                    {collapsedGroups.size === 0 ? (
                      <ChevronsDownUp className="h-4 w-4" />
                    ) : (
                      <ChevronsUpDown className="h-4 w-4" />
                    )}
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>{collapsedGroups.size === 0 ? "Collapse all" : "Expand all"}</p>
                </TooltipContent>
              </Tooltip>
            </div>

            <div className="w-8 flex-shrink-0">
              <Checkbox
                checked={
                  mergeSelectedFiles.length === files.length && files.length > 0
                }
                onCheckedChange={(checked) => {
                  onSelectAllForMerge?.(checked === true);
                }}
              />
            </div>

            <SortableColumnHeader
              column="short_summary"
              label="AI Description"
              sortState={sorting.sortState}
              onSort={sorting.handleSort}
              className="w-1/5 min-w-[160px]"
            />
            <SortableColumnHeader
              column="date"
              label="Document Date"
              sortState={sorting.sortState}
              onSort={sorting.handleSort}
              className="w-1/6 min-w-[100px]"
            />
            <SortableColumnHeader
              column="created_at"
              label="Uploaded Date"
              sortState={sorting.sortState}
              onSort={sorting.handleSort}
              className="w-1/6 min-w-[100px]"
            />
            <SortableColumnHeader
              column="name"
              label="File Name"
              sortState={sorting.sortState}
              onSort={sorting.handleSort}
              className={
                showSummaryPanel ? "w-2/5 min-w-[120px]" : "w-1/6 min-w-[120px]"
              }
            />

            {!showSummaryPanel && (
              <div className="flex w-1/4 min-w-[240px] shrink-0 text-sm font-medium text-foreground-muted">
                <div className="flex items-center truncate">Actions</div>
              </div>
            )}
          </div>

          <div className="w-full min-w-0 p-2">
            {allCategories.map((category) => {
              const filesInCategory = sorting.getSortedData(
                groupedFiles[category] || [],
                sortColumns
              );
              const isCollapsed = collapsedGroups.has(category);
              const isDropTarget = dragState.dragOverZone === category;
              const canDrop = dragState.isDragging && canDropInZone(category);

              const docType = DOCUMENT_CLASSIFICATIONS.find(
                (dt) => dt.category === category
              );
              const groupLabel = docType ? docType.title : category;
              const hasMultipleLOI =
                category === DocumentCategory.LOI && filesInCategory.length > 1;

              return (
                <div
                  key={category}
                  className={`document-type-group mb-3 ${
                    isDropTarget && canDrop
                      ? "rounded-lg border-2 border-dashed border-primary bg-primary/5"
                      : ""
                  }`}
                  onDragOver={(e) => canDrop && handleDragOver(e, category)}
                  onDragEnter={(e) => canDrop && handleDragEnter(e, category)}
                  onDragLeave={(e) => canDrop && handleDragLeave(e)}
                  onDrop={(e) => canDrop && handleCategoryDrop(e, category)}
                >
                  <div className="w-full min-w-0">
                    <DocumentTypeHeader
                      title={groupLabel}
                      count={filesInCategory.length}
                      isCollapsed={isCollapsed}
                      onToggleCollapse={() => toggleGroupCollapse(category)}
                      warningMessage={
                        hasMultipleLOI
                          ? `Multiple LOI files detected (${filesInCategory.length}). Only one Letter of Instruction can be used to generate a report. Please reclassify or delete the extra files so a single LOI remains.`
                          : undefined
                      }
                    />
                  </div>

                  {!isCollapsed && filesInCategory.length > 0 && (
                    <div className="w-full min-w-0 space-y-3">
                      {filesInCategory.map((file) => (
                        <div
                          key={file.id}
                          draggable
                          onDragStart={(e) => {
                            e.dataTransfer.effectAllowed = "move";
                            e.dataTransfer.setData("text/plain", file.id);
                            handleFileDragStart(file, category);
                          }}
                          onDragEnd={handleDragEnd}
                          className={cn(
                            "mt-[15px] flex w-full min-w-[800px] cursor-pointer items-center rounded-xl bg-background p-2 transition-all duration-200",
                            {
                              "bg-primary-50": mergeSelectedFiles.includes(file.id),
                              "hover:bg-primary/10":
                                selectedDocumentId !== file.id &&
                                !mergeSelectedFiles.includes(file.id),
                            },
                            hasMultipleLOI && "bg-warning-50 ring-1 ring-warning/40",
                            dragState.dragItem?.id === file.id &&
                              "scale-95 opacity-50 shadow-lg",
                            "cursor-grab active:cursor-grabbing"
                          )}
                          onClick={() => {
                            if (selectedDocumentId === file.id) {
                              onViewFile(null);
                            } else {
                              onViewFile(file);
                            }
                          }}
                        >
                          {/* Checkbox and Drag Handle - both shown in merge mode */}
                          <div className="flex items-center gap-2">
                            <div className="flex flex-shrink-0 justify-center">
                              <figure className="cursor-grab text-foreground-muted transition-colors hover:text-foreground">
                                <DragHandleIcon className="h-4 w-4" />
                              </figure>
                            </div>

                            <div className="flex flex-shrink-0 justify-center px-2">
                              <Checkbox
                                checked={mergeSelectedFiles.includes(file.id)}
                                onCheckedChange={(checked) => {
                                  onSelectFileForMerge?.(file.id, checked === true);
                                }}
                                onClick={(e) => e.stopPropagation()}
                              />
                            </div>
                          </div>

                          {/* Document Description Column */}
                          <div
                            className="w-1/5 min-w-[160px] overflow-hidden p-3"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <SummaryStatusCell
                              file={file}
                              onEdit={handleEditDescription}
                            />
                          </div>

                          {/* Document Date Column */}
                          <div className="w-1/6 min-w-[100px] overflow-hidden p-3 text-sm text-foreground">
                            <div className="flex items-center gap-2">
                              <div className="truncate">
                                {formatNullableDateToLocal(file.date)}
                              </div>
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleEditDate(file);
                                }}
                                className="h-6 w-6 p-0 text-foreground-muted hover:text-foreground"
                                title="Edit Document Date"
                              >
                                <Pencil className="h-3 w-3" />
                              </Button>
                            </div>
                          </div>

                          {/* Uploaded Date Column */}
                          <div className="w-1/6 min-w-[100px] overflow-hidden p-3 text-sm text-foreground">
                            <div className="truncate">
                              {formatNullableDateToLocal(file.created_at)}
                            </div>
                          </div>

                          {/* File Name Column */}
                          <div
                            className={cn(
                              "min-w-[120px] overflow-hidden p-3 text-sm text-foreground",
                              showSummaryPanel ? "w-2/5" : "w-1/6"
                            )}
                          >
                            <div className="flex w-full items-center gap-2 overflow-hidden">
                              <FileIcon
                                fileName={stripBundlePrefix(file.name)}
                                className="h-5 w-5 flex-shrink-0"
                              />
                              <span className="truncate font-medium text-foreground">
                                {stripBundlePrefix(file.name)}
                              </span>

                              {/* Status icons */}
                              {file.status === "EXPORTED" && (
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <figure className="rounded-full bg-primary-50 p-1">
                                      <Check className="size-3 text-primary" />
                                    </figure>
                                  </TooltipTrigger>
                                  <TooltipContent>
                                    <p>
                                      This file has been exported to Report Builder.
                                    </p>
                                  </TooltipContent>
                                </Tooltip>
                              )}

                              {file.status === "EXPORTING" && (
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <figure className="rounded-full bg-warning-50 p-1">
                                      <Upload className="size-3 animate-pulse text-warning" />
                                    </figure>
                                  </TooltipTrigger>
                                  <TooltipContent>
                                    <p>
                                      This file is currently being exported to Edge
                                      Compose.
                                    </p>
                                  </TooltipContent>
                                </Tooltip>
                              )}

                              {/* More Options Menu - shown when showSummaryPanel is true */}
                              {showSummaryPanel && (
                                <div
                                  className="ml-auto flex-shrink-0"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  <Popover
                                    open={openPopoverId === file.id}
                                    onOpenChange={(open) => {
                                      setOpenPopoverId(open ? file.id : null);
                                    }}
                                  >
                                    <PopoverTrigger asChild>
                                      <Button
                                        variant="ghost"
                                        size="icon"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                        }}
                                        className="h-8 w-8 p-[4px] text-foreground-muted hover:text-foreground"
                                        title="More options"
                                      >
                                        <MoreVertical className="h-4 w-4" />
                                      </Button>
                                    </PopoverTrigger>
                                    <PopoverContent
                                      className="w-48 p-1"
                                      align="end"
                                      onClick={(e) => e.stopPropagation()}
                                    >
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleOpenManualClassification(file);
                                        }}
                                        className="w-full rounded-md px-3 py-2 text-left text-sm hover:bg-background-muted"
                                      >
                                        Manual Classification
                                      </button>
                                      {onUnmergeFile && file.is_merged_file && (
                                        <button
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            setOpenPopoverId(null);
                                            onUnmergeFile(file.id, file.name);
                                          }}
                                          className="w-full rounded-md px-3 py-2 text-left text-sm hover:bg-background-muted"
                                        >
                                          Unmerge
                                        </button>
                                      )}
                                    </PopoverContent>
                                  </Popover>
                                </div>
                              )}
                            </div>
                          </div>

                          {/* Actions Column */}
                          {!showSummaryPanel && (
                            <div className="w-1/4 min-w-[240px] overflow-visible p-3 text-sm text-foreground">
                              <div
                                className="flex items-center justify-start gap-2 overflow-visible"
                                onClick={(e) => e.stopPropagation()}
                              >
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    onViewSummary(file);
                                  }}
                                  className="w-auto px-2 text-primary"
                                  title="View Full Summary"
                                >
                                  <Eye className="h-4 w-4" /> View Summary
                                </Button>

                                <div onClick={(e) => e.stopPropagation()}>
                                  <DeleteFileConfirmation
                                    file={{
                                      id: file.id,
                                      name: file.name,
                                      classification: file.classification || "",
                                      date: file.date || undefined,
                                      case: file.case,
                                    }}
                                  />
                                </div>

                                {/* More Options Menu */}
                                <Popover
                                  open={openPopoverId === file.id}
                                  onOpenChange={(open) => {
                                    setOpenPopoverId(open ? file.id : null);
                                  }}
                                >
                                  <PopoverTrigger asChild>
                                    <Button
                                      variant="ghost"
                                      size="icon"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                      }}
                                      className="h-8 w-8 p-[4px] text-foreground-muted hover:text-foreground"
                                      title="More options"
                                    >
                                      <MoreVertical className="h-4 w-4" />
                                    </Button>
                                  </PopoverTrigger>
                                  <PopoverContent
                                    className="w-48 p-1"
                                    align="end"
                                    onClick={(e) => e.stopPropagation()}
                                  >
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleOpenManualClassification(file);
                                      }}
                                      className="w-full rounded-md px-3 py-2 text-left text-sm hover:bg-background-muted"
                                    >
                                      Manual Classification
                                    </button>
                                    {onUnmergeFile && file.is_merged_file && (
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setOpenPopoverId(null);
                                          onUnmergeFile(file.id, file.name);
                                        }}
                                        className="w-full rounded-md px-3 py-2 text-left text-sm hover:bg-background-muted"
                                      >
                                        Unmerge
                                      </button>
                                    )}
                                  </PopoverContent>
                                </Popover>
                              </div>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        <ScrollBar orientation="vertical" />
        <ScrollBar orientation="horizontal" />
      </ScrollArea>

      {/* Edit Document Date Modal */}
      <EditDocumentDateModal
        file={editingFile}
        isOpen={isEditDateModalOpen}
        onClose={handleCloseEditDateModal}
      />

      {/* Edit Document Description Modal */}
      <EditDocumentDescriptionModal
        file={editingDescriptionFile}
        isOpen={isEditDescriptionModalOpen}
        onClose={handleCloseEditDescriptionModal}
      />

      {/* Manual Classification Modal */}
      <ManualClassificationModal
        file={manualClassificationFile}
        isOpen={isManualClassificationModalOpen}
        onClose={handleCloseManualClassificationModal}
        onSave={handleManualClassificationSave}
      />
    </>
  );
};
